import React, { useEffect, useRef } from 'react';
import {
    Box,
    Button,
    Link,
    Stack,
    StatusMessage,
    SubtleText,
    useFloatingAlert,
} from '@99designs/emu';
import { FormattedReactMessage } from '@99designs/frontend-components/i18n';
import { getSupportContactUrl } from '@99designs/frontend-components/utils/platform/supportUrlHelpers';
import { useTwoFactorUI } from './hooks';
import { ErrorType } from './types';

const errorMessages: { [type in ErrorType]: string } = {
    TOKEN_ATTEMPTS: __('Too many failed attempts. Please <Link>request a new code</Link>'),
    TOKEN_EMPTY: __('Please enter your security code'),
    TOKEN_EXPIRED: __('This code is no longer valid. Please <Link>request a new code</Link>'),
    TOKEN_INVALID: __('Your code is invalid. Please try again'),
};

export function VerifyAddPayoutForm() {
    const { resendRequestCount, resendToken, verifyToken, uiState, error } = useTwoFactorUI();

    const inputEl = useRef<HTMLInputElement>(null);
    const floatingAlert = useFloatingAlert();

    useEffect(() => {
        if (uiState === 'ERROR' && error === null) {
            floatingAlert({
                type: 'error',
                message: (
                    <FormattedReactMessage
                        message={__('Something went wrong, please <Link>contact support</Link>')}
                        replacements={{
                            Link: <Link color="white" href={getSupportContactUrl(__LANG__)} />,
                        }}
                    />
                ),
            });
        } else if (uiState === 'SUCCESS' || uiState === 'RESEND_SUCCESS') {
            floatingAlert({
                type: 'success',
                message:
                    uiState === 'RESEND_SUCCESS'
                        ? __('Code re-sent')
                        : __('Code accepted. Redirecting... '),
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [error, uiState]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const t = inputEl?.current ? inputEl.current.value : '';
        verifyToken(t);
    };

    return (
        <>
            <form onSubmit={handleSubmit}>
                <div
                    className="field"
                    data-field-error={uiState === 'ERROR' && error ? true : undefined}
                >
                    <label className="field__title" htmlFor="security-code">
                        {__('Enter code')}
                    </label>
                    {uiState === 'ERROR' && error && (
                        <StatusMessage type="error">
                            <span data-testid="error-message">
                                <FormattedReactMessage
                                    message={errorMessages[error]}
                                    replacements={{
                                        Link: (
                                            <Link
                                                data-testid="resend-token-error"
                                                color="risingSun"
                                                onClick={resendToken}
                                            />
                                        ),
                                    }}
                                />
                            </span>
                        </StatusMessage>
                    )}
                    <div className="field__input">
                        <input
                            ref={inputEl}
                            aria-label={__('Enter security code')}
                            placeholder={__('Security code')}
                            id="security-code"
                            name="token"
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]{6}"
                            autoComplete="one-time-code"
                            className="input-text input-width--short"
                        />
                    </div>
                </div>
                <Box marginTop="3bu" marginBottom="1bu">
                    <Button
                        data-testid="verify-token"
                        appearance="primary"
                        loading={uiState === 'LOADING'}
                        disabled={uiState === 'LOADING' || uiState === 'RESEND_LOADING'}
                        type="submit"
                    >
                        {__('Submit')}
                    </Button>
                </Box>
            </form>
            <Box marginTop="2bu" marginBottom="1bu">
                <Stack space="0.75bu">
                    <SubtleText>
                        <FormattedReactMessage
                            message={
                                resendRequestCount === 0
                                    ? __("Didn't receive a code? <Link>Resend code</Link>")
                                    : __("Still didn't receive a code? <Link>Resend code</Link>")
                            }
                            replacements={{
                                Link: (
                                    <Link
                                        data-testid="resend-token"
                                        color="grey700"
                                        onClick={resendToken}
                                        size="size2"
                                    />
                                ),
                            }}
                        />
                    </SubtleText>
                    {resendRequestCount > 1 && (
                        <SubtleText>
                            <FormattedReactMessage
                                message={__(
                                    "Make sure to check your spam folder. If you still haven't received a code please <Link>contact support</Link>"
                                )}
                                replacements={{
                                    Link: (
                                        <Link
                                            color="grey700"
                                            href={getSupportContactUrl(__LANG__)}
                                            size="size2"
                                        />
                                    ),
                                }}
                            />
                        </SubtleText>
                    )}
                </Stack>
            </Box>
        </>
    );
}
